import React from "react";
import { useAuth } from "react-oidc-context";
import {
  useNitroCAStore,
  TariffRowStatus,
} from "../../../store/CompetitiveAnalysisStore";
import NitroService from "../../../utils/nitro.service";
import {
  FreightRail,
  IDestinationExtension,
  IQuoteExtension,
} from "../../../types/IQuotes";
import { useNitroStore } from "../../../store/Store";
import RouteValidationService from "../../competitiveAnalysisModal/components/RouteValidation/lib/service";
import { findTypeMatchedOccupant } from "../../mobile/multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import { toMobileQuote } from "./multiLocationQuoteUtils";

type UseTariffPollOptions = {
  moveToOriginTable?: (params: {
    rowKey: string;
    quote: IQuoteExtension;
    foundQuote: IQuoteExtension;
    destination: IDestinationExtension;
  }) => void;
};

const POLL_DURATION_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = POLL_DURATION_SECONDS / (POLL_INTERVAL_MS / 1000); // 8

export function useTariffPoll(options: UseTariffPollOptions = {}) {
  const { moveToOriginTable } = options;
  const [setTariffStateForRow] = useNitroCAStore((s) => [
    s.setTariffStateForRow,
  ]);
  const activeIntervalIds = React.useRef<Record<string, number>>({});
  const activeCountdownIds = React.useRef<Record<string, number>>({});
  React.useEffect(() => {
    const intervals = activeIntervalIds.current;
    const countdowns = activeCountdownIds.current;
    return () => {
      Object.values(intervals).forEach(window.clearInterval);
      Object.values(countdowns).forEach(window.clearInterval);
    };
  }, []);

  const [product, currency, uom, currentFilters] = useNitroStore((state) => [
    state.product,
    state.currency,
    state.uom,
    state.currentFilters,
  ]);
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;
  const startTariffPoll = React.useCallback(
    (
      rowKey: string,
      quote: IQuoteExtension,
      destination: IDestinationExtension,
      pollOptions?: { silent?: boolean },
    ) => {
      const silent = pollOptions?.silent ?? false;

      // Silent polls skip all tariffStateByRowKey writes, so the local ref is
      // the only re-entrancy guard available for them.
      if (activeIntervalIds.current[rowKey] !== undefined) {
        return;
      }

      if (!silent) {
        // Guard: use store status so the check is shared across components
        const currentStatus =
          useNitroCAStore.getState().tariffStateByRowKey[rowKey]?.status;
        if (currentStatus === "checking") {
          return;
        }
      }

      // Reserve in the local ref to block double-clicks before the setTimeout fires
      activeIntervalIds.current[rowKey] = -1;

      if (!silent) {
        setTariffStateForRow(rowKey, {
          status: "checking",
          secondsRemaining: POLL_DURATION_SECONDS,
        });

        // Start a 1s countdown so the UI can display remaining time
        let secondsLeft = POLL_DURATION_SECONDS;
        const countdownId = window.setInterval(() => {
          secondsLeft -= 1;
          const currentTimerStatus =
            useNitroCAStore.getState().tariffStateByRowKey[rowKey]?.status;
          if (currentTimerStatus !== "checking") {
            window.clearInterval(countdownId);
            delete activeCountdownIds.current[rowKey];
            return;
          }
          setTariffStateForRow(rowKey, {
            status: "checking",
            secondsRemaining: Math.max(0, secondsLeft),
          });
        }, 1000);
        activeCountdownIds.current[rowKey] = countdownId;
      }

      let pollCount = 0;
      const intervalId = window.setInterval(async () => {
        pollCount += 1;

        const occupants =
          quote.origin.details && Array.isArray(quote.origin.details.occupants)
            ? quote.origin.details.occupants
            : [];
        const matchedOccupant = findTypeMatchedOccupant(
          occupants,
          toMobileQuote(quote),
        );
        const result = await NitroService.getQuotes({
          id: destination.id,
          originId: quote.origin.id,
          product: product || "UAN",
          party: null,
          marketer: user,
          shipDate: currentFilters.month || "",
          currency: currency || "USD",
          uom: uom || "ST",
        });
        const validation = await RouteValidationService.validate({
          mode: "RAIL",
          product: product || "UAN",
          originLocationId: quote.origin.id,
          originOccupantId: matchedOccupant?.id,
          destinationId: destination.id,
          shipDate: currentFilters.month || "",
          isCity: destination.isCity,
        });

        // status checking
        // search was completed but no rate found
        if (validation.railRateRequestFailed) {
          if (!silent) {
            setTariffStateForRow(rowKey, {
              status: "not found",
              rate: undefined,
            });
          }
          window.clearInterval(intervalId);
          delete activeIntervalIds.current[rowKey];
          window.clearInterval(activeCountdownIds.current[rowKey]);
          delete activeCountdownIds.current[rowKey];
          return;
        }

        // rate is either found or ash -> nitro process hasn't completed yet.
        if (result.length > 0) {
          // check for found rail rate and for each one add it to the origin table
          result[0].quotes
            .filter(
              (q) =>
                q.mode === "RAIL" &&
                q.origin.id === quote.origin.id &&
                q.competitor === quote.competitor,
            )
            .map((q) => {
              const freight = q.freight as FreightRail | undefined;

              // rate is found
              if (freight?.totalFreight) {
                // if the rate is for a competitor quote, or it is an internal koch rate then add the quote to the all origins table
                if (q.competitor || (!q.competitor && !freight.isExternal)) {
                  moveToOriginTable?.({
                    rowKey,
                    quote,
                    foundQuote: q,
                    destination,
                  });
                }
                if (!silent) {
                  setTariffStateForRow(rowKey, {
                    status: "found",
                    rate: String(freight.totalFreight.toFixed(2)),
                  });
                }
                window.clearInterval(intervalId);
                delete activeIntervalIds.current[rowKey];
                window.clearInterval(activeCountdownIds.current[rowKey]);
                delete activeCountdownIds.current[rowKey];
              }
              return null;
            });
        }

        // clear found/failed
        const clearRow = () => {
          window.clearInterval(intervalId);
          delete activeIntervalIds.current[rowKey];
          window.clearInterval(activeCountdownIds.current[rowKey]);
          delete activeCountdownIds.current[rowKey];
        };

        // rate search hasn't completed yet
        if (pollCount >= MAX_POLLS) {
          if (!silent) {
            setTariffStateForRow(rowKey, {
              status: "try again",
              rate: undefined,
            });
          }
          clearRow();
        }
      }, POLL_INTERVAL_MS);

      activeIntervalIds.current[rowKey] = intervalId;
    },
    [
      currency,
      currentFilters.month,
      moveToOriginTable,
      product,
      setTariffStateForRow,
      uom,
      user,
    ],
  );

  return { startTariffPoll };
}

// Re-export the type so consumers don't need a separate store import
export type { TariffRowStatus };
